/**
 * Role hierarchy — `superadmin` implicitly has every privilege that
 * `finance_ops` has. This file is the single source of truth for that rule;
 * use `canAccess` wherever you'd otherwise write `role === 'x' || role === 'y'`.
 */

const ROLE_ALIASES = {
  // demo_introducing_agent gets the same access as introducing_agent
  introducing_agent: ['demo_introducing_agent'],
  // superadmin inherits finance_ops — so any route allowing finance_ops
  // also admits superadmin by default.
  finance_ops: ['superadmin'],
};

/**
 * canAccess(userRole, allowed) — true if userRole is in the allowed list,
 * OR if userRole is a higher-privileged role that implicitly covers it.
 *
 * Examples:
 *   canAccess('superadmin',  'finance_ops')                       → true
 *   canAccess('finance_ops', 'finance_ops')                       → true
 *   canAccess('finance_ops', 'superadmin')                        → false
 *   canAccess('superadmin',  ['superadmin', 'finance_ops'])       → true
 *   canAccess('demo_introducing_agent', 'introducing_agent')      → true
 */
function canAccess(userRole, allowed) {
  if (!userRole || !allowed) return false;
  const needs = Array.isArray(allowed) ? allowed : [allowed];
  for (const need of needs) {
    if (userRole === need) return true;
    const inheritors = ROLE_ALIASES[need] || [];
    if (inheritors.includes(userRole)) return true;
  }
  return false;
}

/**
 * Which dashboards a user can access — drives the switcher visibility.
 * Returns an array of { key, label, path } that the switcher will render.
 */
function dashboardsForRole(userRole) {
  const out = [];
  if (canAccess(userRole, 'superadmin')) {
    out.push({ key: 'admin', label: 'Admin', path: '/admin-home' });
  }
  if (canAccess(userRole, 'finance_ops')) {
    out.push({ key: 'finance_ops', label: 'Finance Ops', path: '/finance-ops-home' });
  }
  return out;
}

export { canAccess, dashboardsForRole };
